import { Component } from '@theme/component';


// Native horizontal scrolling lets touch gestures and momentum control the brand strip.
function initializeBrandScroll(viewport, signal) {
  const track = viewport.querySelector('.flery-brand-track');
  const group = track?.querySelector('.flery-brand-group');
  if (!group) return;
  const extra = track.querySelector('.flery-brand-copy').cloneNode(true);
  track.append(extra);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const options = { signal, passive: true };
  let overflowing = false;
  let frame = 0, span = 0, last = 0, expected = 0, position = 0, resumeAt = 0, touching = false;
  const write = (left) => { position = left; viewport.scrollLeft = left; expected = viewport.scrollLeft; };
  const normalize = () => {
    if (!overflowing || !span) return;
    if (viewport.scrollLeft < span / 2) write(viewport.scrollLeft + span);
    else if (viewport.scrollLeft >= span * 1.5) write(viewport.scrollLeft - span);
  };
  const tick = (now) => {
    if (signal.aborted || !overflowing || reduced.matches) { frame = 0; return; }
    if (!reduced.matches && !document.hidden && !touching && now >= resumeAt && span && last) {
      // One full set passes in 48 seconds, matching the original gentle pace.
      write(position + span / 48000 * Math.min(now - last, 64));
      normalize();
    }
    last = now;
    frame = requestAnimationFrame(tick);
  };
  const start = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    last = 0;
    const contentWidth = group.getBoundingClientRect().width - parseFloat(getComputedStyle(group).paddingInlineEnd);
    const nextOverflow = contentWidth > viewport.clientWidth + .5;
    const changed = nextOverflow !== overflowing;
    overflowing = nextOverflow;
    viewport.classList.toggle('is-overflowing', overflowing);
    if (!overflowing) { span = 0; write(0); return; }
    const nextSpan = group.getBoundingClientRect().width;
    if (changed || Math.abs(nextSpan - span) > .5) { span = nextSpan; write(span); }
    if (!reduced.matches) frame = requestAnimationFrame(tick);
  };
  const begin = () => { touching = true; resumeAt = performance.now() + 1500; };
  const end = () => { touching = false; resumeAt = performance.now() + 1500; };
  viewport.addEventListener('pointerdown', begin, options);
  window.addEventListener('pointerup', end, options);
  window.addEventListener('pointercancel', end, options);
  viewport.addEventListener('touchstart', begin, options);
  viewport.addEventListener('touchend', end, options);
  viewport.addEventListener('touchcancel', end, options);
  viewport.addEventListener('wheel', () => { resumeAt = performance.now() + 1500; }, options);
  viewport.addEventListener('scroll', () => {
    if (Math.abs(viewport.scrollLeft - expected) > .5) { position = viewport.scrollLeft; resumeAt = performance.now() + 1500; }
    normalize(); expected = viewport.scrollLeft;
  }, options);
  const observer = new ResizeObserver(start);
  observer.observe(group);
  observer.observe(viewport);
  reduced.addEventListener('change', start, { signal });
  signal.addEventListener('abort', () => { cancelAnimationFrame(frame); observer.disconnect(); extra.remove(); viewport.classList.remove('is-overflowing'); }, { once: true });
  start();
}

class FleryBoutique extends Component {
  connectedCallback() { super.connectedCallback(); this.initialize(); }
  updatedCallback() { super.updatedCallback(); this.initialize(); }
  disconnectedCallback() { this.controller?.abort(); super.disconnectedCallback(); }
  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    for (const viewport of this.querySelectorAll('.flery-brand-viewport')) initializeBrandScroll(viewport, this.controller.signal);
    this.addEventListener('change', (event) => {
      const toggle = event.target;
      if (!(toggle instanceof HTMLInputElement) || !toggle.matches('[data-pet-toggle]')) return;
      const group = toggle.closest('.flery-pet-group');
      if (!group) return;
      const cat = toggle.checked;
      for (const item of group.querySelectorAll('.show-cat')) item.classList.toggle('pet-hidden', !cat);
      for (const item of group.querySelectorAll('.show-dog')) item.classList.toggle('pet-hidden', cat);
      for (const label of group.querySelectorAll('.cat-label')) label.classList.toggle('active-label', cat);
      for (const label of group.querySelectorAll('.dog-label')) label.classList.toggle('active-label', !cat);
      const link = group.querySelector('[data-bowl-link]');
      if (link) link.href = cat ? link.dataset.catUrl : link.dataset.dogUrl;
    }, options);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rail-direction]');
      if (!button) return;
      const rail = button.closest('.flery-product-region')?.querySelector('.flery-rail');
      if (!rail) return;
      rail.scrollBy({ left: Number(button.dataset.railDirection) * rail.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, options);
  }
}

class FleryHeader extends Component {
  connectedCallback() { super.connectedCallback(); this.initialize(); }
  updatedCallback() { super.updatedCallback(); this.initialize(); }
  disconnectedCallback() { this.controller?.abort(); super.disconnectedCallback(); }
  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    const scroller = this.closest('.page-wrapper') || document.scrollingElement;
    const update = () => this.classList.toggle('flery-scrolled', Math.max(window.scrollY, scroller?.scrollTop || 0) > 120);
    window.addEventListener('scroll', update, { ...options, passive: true });
    scroller?.addEventListener('scroll', update, { ...options, passive: true });
    update();
    for (const details of this.querySelectorAll('.flery-desktop-menu details')) {
      details.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') details.open = true; }, options);
      details.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') details.open = false; }, options);
    }
    const drawer = this.querySelector('.flery-mobile-drawer');
    const localization = this.querySelector('.flery-header-localization');
    const resetMenu = () => {
      this.querySelector('[data-menu-root]').hidden = false;
      for (const panel of this.querySelectorAll('[data-menu-section]')) panel.hidden = true;
    };
    const restoreScroll = () => document.documentElement.classList.remove('flery-drawer-open');
    drawer.addEventListener('close', restoreScroll, options);
    localization.addEventListener('close', restoreScroll, options);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (button?.matches('[data-menu-open]')) {
        resetMenu(); drawer.showModal(); document.documentElement.classList.add('flery-drawer-open');
      }
      if (button?.matches('[data-menu-close]') || event.target === drawer) drawer.close();
      if (button?.matches('[data-menu-search]')) {
        drawer.close(); this.querySelector('.search-action button').click();
      }
      if (button?.matches('[data-menu-panel]')) {
        this.querySelector('[data-menu-root]').hidden = true;
        const panel = this.querySelector(`[data-menu-section="${button.dataset.menuPanel}"]`);
        panel.hidden = false; panel.querySelector('button').focus();
      }
      if (button?.matches('[data-menu-back]')) {
        const key = button.closest('[data-menu-section]').dataset.menuSection;
        resetMenu(); this.querySelector(`[data-menu-panel="${key}"]`).focus();
      }
      if (button?.matches('[data-localization-open]')) {
        localization.showModal(); document.documentElement.classList.add('flery-drawer-open');
      }
      if (button?.matches('[data-localization-close]') || event.target === localization) localization.close();
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const details = event.target.closest('details[open]');
      if (details) { details.open = false; details.querySelector('summary')?.focus(); }
    }, options);
  }
}

if (!customElements.get('flery-boutique')) customElements.define('flery-boutique', FleryBoutique);
if (!customElements.get('flery-header')) customElements.define('flery-header', FleryHeader);
